import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ColumnValueChangedEvent, GridReadyEvent } from "ag-grid-community";
import { ColDef, ExcelStyle, GetRowIdParams, GridApi, ProcessCellForExportParams, RowClassParams, SideBarDef, themeQuartz } from "ag-grid-community";
import { SearchableSelect, SelectOption } from "@/lib/shadcn/components/searchable-select";
import { ColumnState, Convert, GridView } from "@/models/responses";
import { errorPopup, successPopup } from "@/utils/alerts";
import { decodeErrorResponse } from "@/utils/errors";
import { useAppData } from "@/hooks/useAppData";
import { Card } from "@/lib/shadcn/ui/card";
import { Input } from "@/lib/shadcn/ui/input";
import { Download, Search } from "lucide-react";
import SaveViewDialog from "@/lib/shadcn/components/save-view-dialog";
import GridStats, { ColumnToAggregate } from "./grid-stats";
import { DropdownActions } from "@/lib/shadcn/components/dropdown-actions";
import { AgGridReact } from "ag-grid-react";
import { Button } from "@/lib/shadcn/ui/button";
import { fetchOptions, getApiBasePath } from "@/hooks/fetchOptions";
import { apiUrl } from "@/layouts/apiUrl";

export type AggregateFunction = "sum" | "avg";

export interface GridDropdownFilter {
    label: string;
    options: SelectOption[];
    selectedOption: string;
    onSelectionChanged: (selection: string) => void;
    /** Optional stable hook placed on the select trigger, for tests. */
    testId?: string;
}

export interface GridButton {
    label: string;
    onClick: () => void;
    icon: React.FC<{ className?: string }>;
}

export interface SelectedRowsActions {
    label: string;
    onClick: () => void;
    icon: React.FC<{ className?: string }>;
}

export interface GridProps<T> {
    columnFilterText?: string;
    initialRowData: T[];
    columnDefs: ColDef[];
    gridName: string;
    canExport?: boolean;
    dropdownFilters?: GridDropdownFilter[];
    additionalFilters?: React.ReactNode;
    showAggregateFilter?: boolean;
    hideSavedViews?: boolean;
    buttons?: GridButton[];
    actions?: SelectedRowsActions[];
    getRowId?: (params: GetRowIdParams<T>) => string;
    onGridReady?: (gridApi: GridApi<T>) => void;
    columnsToAggregate?: ColumnToAggregate[];
    /** Optional per-row CSS class hook (e.g. muting cancelled reservations). */
    getRowClass?: (params: RowClassParams<T>) => string | string[] | undefined;
}

/**
 * A copy of a stored column state with its sort stripped. AG Grid leaves a
 * property alone when it is absent from the applied state, so this yields a
 * "everything except the sort" patch.
 */
function withoutSort(state: ColumnState): ColumnState {
    const copy: Partial<ColumnState> = { ...state };
    delete copy.sort;
    delete copy.sortIndex;
    return copy as ColumnState;
}

const Grid = <T extends object>(props: GridProps<T>) => {
    const { selectedPeriod, selectedYear } = useAppData();

    const [quickFilterText, setQuickFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState<T[]>([]);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [selectViewOptions, setSelectViewOptions] = useState<{ label: string; value: string }[]>([]);
    const [selectedView, setSelectedView] = useState<GridView>();
    const [savedViews, setSavedViews] = useState<GridView[]>([]);
    const [saveViewDialogOpen, setSaveViewDialogOpen] = useState(false);
    const [pendingViewSnapshot, setPendingViewSnapshot] = useState<{
        columnState: ColumnState[];
        filterModel: { [key: string]: unknown };
    } | null>(null);
    const [isViewModified, setIsViewModified] = useState(false);
    const exportedExcelName = `${props.gridName}-${selectedPeriod.toLowerCase()}-${selectedYear}`;
    const canExport = props.canExport === undefined ? true : props.canExport;
    const gridName = props.gridName.toLowerCase().replace(" ", "_");

    const defaultColDef = useMemo<ColDef>(() => {
        return {
            enableRowGroup: true,
            enablePivot: true,
            cellStyle: {
                textAlign: "left",
                paddingLeft: "18px",
                fontSize: "13px",
            },
            enableValue: true,
            filter: true,
            resizable: true,
            sortable: true,
            suppressMovable: false, // Allow column reordering
            autoHeight: true, // Auto-height for wrapped content
            wrapHeaderText: true,
            autoHeaderHeight: true,
            floatingFilter: true,
            width: 100,
        };
    }, []);

    const sideBar: SideBarDef = useMemo<SideBarDef>(() => {
        return {
            toolPanels: [
                {
                    id: "columns",
                    labelDefault: "Columns",
                    labelKey: "columns",
                    iconKey: "columns",
                    toolPanel: "agColumnsToolPanel",
                },
                {
                    id: "filters",
                    labelDefault: "Filters",
                    labelKey: "filters",
                    iconKey: "filter",
                    toolPanel: "agFiltersToolPanel",
                },
            ],
        };
    }, []);

    /**
     * Apply a view's stored column state (order, width, visibility, sort).
     *
     * `preserveSort` re-applies everything EXCEPT sort. A stored ColumnState
     * carries `sort`/`sortIndex`, so replaying it clobbers whatever sort the
     * user has since chosen — a view is typically saved unsorted, so replaying
     * it silently resets the grid to "no sort". Omitting those two keys leaves
     * the current sort untouched (AG Grid ignores properties not present in the
     * state, unless a `defaultState` says otherwise).
     *
     * Sort SHOULD be restored when the user explicitly picks a view; it should
     * NOT be touched when we're merely correcting column order behind their back.
     */
    const applyColumnState = useCallback(
        (view: GridView, { preserveSort = false }: { preserveSort?: boolean } = {}) => {
            if (!gridApi || gridApi.isDestroyed()) return;
            const state = preserveSort ? view.columnStates.map(withoutSort) : view.columnStates;
            gridApi.applyColumnState({ state, applyOrder: true });
        },
        [gridApi],
    );

    // Restore the saved filter model AFTER row data is present. Set-column filters
    // need the row data loaded first to resolve their available values, so this runs
    // in its own effect keyed on rowData + selectedView rather than synchronously with
    // the column state. Backward compatible: views saved before filter persistence
    // carry no filterModel, so we clear any active filter instead. Applying the model
    // fires onFilterChanged (which flags the view dirty), so we reset that flag here.
    //
    // The `appliedFilterViewKey` ref makes this fire only when the SELECTED VIEW (or
    // the row data the filter resolves against) actually changes — NOT on every
    // re-render. Without it, an unrelated re-render (e.g. the user applying a filter,
    // which flips isViewModified) would re-run this effect and wipe the filter the
    // user just set, because the still-default selectedView carries no filterModel.
    const appliedFilterViewKey = useRef<string | null>(null);
    useEffect(() => {
        if (!gridApi || gridApi.isDestroyed()) return;
        const key = `${selectedView?.id ?? ""}:${props.initialRowData.length}`;
        if (appliedFilterViewKey.current === key) return;
        appliedFilterViewKey.current = key;
        if (selectedView?.filterModel) {
            gridApi.setFilterModel(selectedView.filterModel);
        } else {
            gridApi.setFilterModel(null);
        }
        setTimeout(() => {
            setIsViewModified(false);
        }, 0);
    }, [gridApi, selectedView, props.initialRowData]);

    useEffect(() => {
        fetch(`${apiUrl}${getApiBasePath()}/grid/views?gridName=${gridName}`, fetchOptions)
            .then((response) => {
                if (!response.ok) {
                    return decodeErrorResponse(response);
                }
                return response.text();
            })
            .then((response) => {
                // The fetch can resolve after the grid is torn down (route change /
                // unmount). Calling grid APIs on a destroyed grid logs a warning and
                // does nothing useful, so bail out early.
                if (gridApi === null || gridApi.isDestroyed()) return;
                gridApi.resetColumnState();
                const defaultColumnState: ColumnState[] = gridApi.getColumnState() as ColumnState[];
                const defaultView = {
                    id: 0,
                    columnStates: defaultColumnState,
                    viewName: "Default View",
                    gridName: gridName.toLowerCase(),
                };
                let savedViews = [defaultView, ...Convert.toGetSavedGridViewsResponse(response).views];
                setSavedViews(savedViews);
                let viewOptions: SelectOption[] = [];
                savedViews.forEach((view) => {
                    viewOptions.push({ label: view.viewName, value: view.id.toString() });
                });
                setSelectViewOptions(viewOptions);

                const savedViewId = localStorage.getItem(`${gridName}-selectedViewId`);
                const selectedView = savedViews.find((view) => view.id === Number(savedViewId));
                setSelectedView(selectedView || defaultView);
                if (selectedView) {
                    applyColumnState(selectedView);
                    setTimeout(() => {
                        setIsViewModified(false);
                    });
                }
            })
            .catch((error) => console.log(error));
    }, [applyColumnState, gridApi, gridName]);

    const onGridReady = (params: GridReadyEvent) => {
        setGridApi(params.api);
        props.onGridReady?.(params.api);
    };

    const handleQuickFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setQuickFilterText(event.target.value);
    };

    const handleSelectionChanged = () => {
        if (gridApi) {
            setSelectedRows(gridApi.getSelectedRows());
        }
    };

    const excelStyles: ExcelStyle[] = useMemo<ExcelStyle[]>(() => {
        return [
            {
                id: "header",
                alignment: {
                    vertical: "Center",
                },
                font: {
                    size: 16,
                    bold: true,
                },
                interior: {
                    color: "#f8f8f8",
                    pattern: "Solid",
                    patternColor: undefined,
                },
                borders: {
                    borderBottom: {
                        color: "#0a0f37",
                        lineStyle: "Continuous",
                        weight: 1,
                    },
                },
            },
            {
                id: "numberType",
                numberFormat: {
                    format: "0",
                },
            },
            {
                id: "dateType",
                dataType: "DateTime",
                numberFormat: { format: "dd/MM/yyyy" },
            },
            {
                id: "currencyFormat",
                numberFormat: {
                    format: "#,##0.00 €",
                },
            },
            {
                id: "percentFormat",
                numberFormat: {
                    format: "0.00%",
                },
            },
            {
                id: "percentFormatNoMultiplier",
                numberFormat: {
                    format: "0.00##\\%",
                },
            },
            {
                id: "negativeInBrackets",
                numberFormat: {
                    format: "$[blue] #,##0;$ [red](#,##0)",
                },
            },
            {
                id: "booleanType",
                dataType: "Boolean",
            },
            {
                id: "stringType",
                dataType: "String",
            },
        ];
    }, []);

    function handleViewChange(newValue: string) {
        const viewSelection = savedViews.find((view) => view.id === Number(newValue));
        const viewId = Number(newValue);
        if (!viewSelection) {
            console.log("No saved view found: " + viewId);
            return;
        }
        applyColumnState(viewSelection);
        setSelectedView(viewSelection);
        localStorage.setItem(`${gridName}-selectedViewId`, newValue);
        setTimeout(() => {
            setIsViewModified(false);
        }, 0);
    }

    const columnVisibilityChanged = (event: ColumnValueChangedEvent<T>) => {
        if (!event.column) return;
        setIsViewModified(true);
    };

    function openSaveViewDialog() {
        // Snapshot the live grid state BEFORE the modal opens. Opening a modal can
        // momentarily reset the grid's filter model (focus/inert side effects), so
        // capturing here guarantees saveView sees exactly what the user configured.
        setPendingViewSnapshot({
            columnState: (gridApi?.getColumnState() ?? []) as ColumnState[],
            filterModel: gridApi?.getFilterModel() ?? {},
        });
        setSaveViewDialogOpen(true);
    }

    function saveView(viewName: string) {
        // Read the grid state at save time. `pendingViewSnapshot` captures the
        // column + filter state at the moment the toolbar Save was clicked (before
        // the dialog opened); we prefer it so a modal-induced reset of the live grid
        // filter model can't drop the user's filters. Fall back to the live grid for
        // any direct callers.
        const columnState = (pendingViewSnapshot?.columnState ?? gridApi?.getColumnState()) as ColumnState[];
        const filterModel = pendingViewSnapshot?.filterModel ?? gridApi?.getFilterModel();
        const updatedView: GridView | undefined = savedViews.find((view) => viewName === view?.viewName);
        if (columnState === undefined) return;
        fetch(`${apiUrl}${getApiBasePath()}/grid/views`, {
            method: "POST",
            credentials: "include",
            body: Convert.addSavedGridViewRequestToJson({
                columnStates: columnState,
                filterModel: filterModel,
                viewName: viewName,
                gridName: gridName,
                id: updatedView?.id || 0,
            }),
        })
            .then((response) => {
                if (!response.ok) {
                    return decodeErrorResponse(response);
                } else {
                    return response.text();
                }
            })
            .then((response) => {
                const newView = Convert.toAddSavedGridViewResponse(response).gridView;
                if (!updatedView) {
                    setSavedViews([...savedViews, newView]);
                    let viewOptions: SelectOption[] = [];
                    savedViews.forEach((view) => {
                        viewOptions.push({
                            label: view.viewName,
                            value: view.id.toString(),
                        });
                    });
                    viewOptions.push({
                        label: newView.viewName,
                        value: newView.id.toString(),
                    });
                    setSelectViewOptions(viewOptions);
                    setTimeout(() => {
                        setIsViewModified(false);
                        setSelectedView(newView);
                    }, 0);
                    successPopup(`View added successfully.`);
                } else {
                    const updatedSavedViews = savedViews.map((view) => {
                        if (view.id === newView.id) {
                            return newView;
                        } else {
                            return view;
                        }
                    });
                    setSavedViews(updatedSavedViews);
                    successPopup(`View updated successfully.`);
                }
                setSaveViewDialogOpen(false);
                setPendingViewSnapshot(null);
            })
            .catch((error) => {
                void errorPopup(error.message);
            });
    }

    function onDeleteSavedViewClick() {
        if (selectedView?.id === 0) {
            console.log("Cannot delete default view");
            return;
        }
        fetch(`${apiUrl}${getApiBasePath()}/grid/views/${selectedView?.id}`, {
            method: "DELETE",
            credentials: "include",
        })
            .then((response) => {
                if (!response.ok) {
                    return decodeErrorResponse(response);
                }
                return response.text();
            })
            .then((response) => {
                console.log(response);
                const newSavedViews = savedViews.filter((view) => view.id !== selectedView?.id);
                setSavedViews(newSavedViews);
                let viewOptions: SelectOption[] = [];
                newSavedViews.forEach((view) => {
                    viewOptions.push({ label: view.viewName, value: view.id.toString() });
                });
                setSelectViewOptions(viewOptions);
                successPopup("View deleted successfully.");
                setSelectedView(savedViews[0]);
                gridApi?.resetColumnState();
                localStorage.setItem(`${gridName}-selectedViewId`, "0");
                setTimeout(() => {
                    setIsViewModified(false);
                }, 0);
            })
            .catch((error) => errorPopup(error.message));
    }

    return (
        <Card className="bg-white shadow-sm max-w-[92vw] overflow-x-auto">
            {/* Header Section */}
            <div className="p-2 sm:p-4 border-b">
                <div className="flex flex-row sm:flex-col space-y-4">
                    {/* Top Row - Views and Actions */}
                    <div className="flex flex-col sm:flex-row items-center justify-between flex-wrap gap-4">
                        {/* Left side - Views and Column Visibility */}
                        {!props.hideSavedViews && (
                            <div className="flex items-center gap-2">
                                <SearchableSelect
                                    variant="volt"
                                    onChange={handleViewChange}
                                    placeholder="Select View"
                                    selectedValue={selectedView?.id.toString() || "0"}
                                    selectedValuePrefix={"View"}
                                    options={selectViewOptions}
                                    width="w-auto"
                                />
                                <div className="flex gap-2">
                                    <Button variant="volt" size="sm" onClick={openSaveViewDialog} disabled={!isViewModified}>
                                        Save
                                    </Button>
                                    <Button
                                        variant="volt"
                                        size="sm"
                                        onClick={onDeleteSavedViewClick}
                                        disabled={selectedView?.viewName === "Default View"}
                                    >
                                        Delete
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Right side - Filters */}
                        {(props.dropdownFilters || props.additionalFilters) && (
                            <div className="flex flex-row gap-2">
                                {props.additionalFilters}
                                {props.dropdownFilters?.map((filter) => (
                                    <SearchableSelect
                                        key={filter.label}
                                        variant="volt"
                                        onChange={(newVal) => filter.onSelectionChanged(newVal)}
                                        placeholder={filter.label}
                                        selectedValue={filter.selectedOption}
                                        options={filter.options}
                                        width="w-auto"
                                        data-testid={filter.testId}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                    {/* Selected Rows Info */}
                    {selectedRows.length > 0 && (
                        <GridStats<T> selectedRows={selectedRows} columnsToAggregate={props.columnsToAggregate} />
                    )}{" "}
                </div>
            </div>

            {/* Grid Content */}
            <div className="p-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{props.gridName}</h2>
                <div className="flex flex-col sm:flex-row justify-between mb-4">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        {props.buttons?.map((button) => (
                            <Button key={button.label} variant="volt" size="sm" onClick={button.onClick}>
                                <button.icon />
                                {button.label}
                            </Button>
                        ))}
                        {canExport && (
                            <Button
                                variant="volt"
                                size="sm"
                                onClick={() =>
                                    gridApi?.exportDataAsExcel({
                                        fileName: exportedExcelName,
                                        // exportAsExcelTable: true,
                                        headerRowHeight: 30,
                                        columnKeys: gridApi
                                            .getColumnState()
                                            .filter((col) => col.hide !== true)
                                            .filter((col) => col.colId !== "ag-Grid-ControlsColumn")
                                            .map((col) => col.colId),
                                        processCellCallback(params: ProcessCellForExportParams) {
                                            if (params.value === undefined || params.value === null || params.value === "") {
                                                return "";
                                            }
                                            // Handle date columns - format as DD/MM/YYYY for Excel
                                            if (params.column.getColDef().cellDataType === "date") {
                                                try {
                                                    const date = new Date(params.value as string | Date);
                                                    if (isNaN(date.getTime())) {
                                                        return params.value;
                                                    }
                                                    return date.toLocaleDateString("en-GB");
                                                } catch {
                                                    return params.value;
                                                }
                                            }
                                            // Legacy support for cellClass dateType
                                            if (params.column.getColDef().cellClass === "dateType") {
                                                return (params.value as Date).toISOString();
                                            }
                                            return params.value;
                                        },
                                    })
                                }
                                className="flex items-center gap-2"
                            >
                                <Download className="h-4 w-4" />
                                Export
                            </Button>
                        )}
                        {props.actions && props.actions.length > 0 && (
                            // <DropdownMenu>
                            //   <DropdownMenuTrigger asChild>
                            //     <Button
                            //       variant="volt"
                            //       size="sm"
                            //       disabled={selectedRows.length === 0}
                            //     >
                            //       Actions
                            //     </Button>
                            //   </DropdownMenuTrigger>
                            //   <DropdownMenuContent align="end">
                            //     {props.actions.map((action) => (
                            //       <DropdownMenuItem
                            //         key={action.label}
                            //         onClick={action.onClick}
                            //       >
                            //         {action.label}
                            //       </DropdownMenuItem>
                            //     ))}
                            //   </DropdownMenuContent>
                            // </DropdownMenu>
                            <DropdownActions
                                disabled={selectedRows.length === 0}
                                variant="volt"
                                options={props.actions}
                                placeholder={"Actions"}
                                className="w-auto"
                            />
                        )}
                    </div>

                    {/* Right - Search */}
                    <div className={"relative w-64 my-2"}>
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                        <Input placeholder="Search..." onChange={handleQuickFilterChange} className="pl-8" />
                    </div>
                </div>
                <div className="ag-theme-quartz overflow-x-auto h-[800px] rounded-md border border-gray-200">
                    <AgGridReact<T>
                        theme={themeQuartz}
                        onGridReady={onGridReady}
                        rowSelection={{
                            mode: "multiRow",
                        }}
                        rowData={props.initialRowData}
                        defaultColDef={defaultColDef}
                        suppressColumnVirtualisation={false}
                        suppressRowVirtualisation={false}
                        animateRows={true}
                        columnDefs={props.columnDefs}
                        quickFilterText={quickFilterText}
                        paginationPageSizeSelector={[25, 50, 100, 500]}
                        onSelectionChanged={handleSelectionChanged}
                        pagination={true}
                        excelStyles={excelStyles}
                        onSortChanged={() => setIsViewModified(true)}
                        onColumnResized={() => {
                            setIsViewModified(true);
                        }}
                        sideBar={sideBar}
                        onColumnVisible={() => setIsViewModified(true)}
                        onColumnMoved={(col) => {
                            // Re-rendering the grid with a fresh columnDefs identity (e.g. a
                            // page-level filter change, or a row refetch) makes AG Grid
                            // re-apply the incoming column order. The selected view — not
                            // the columnDefs — owns column order, so restore it here.
                            // Crucially this must not disturb the user's sort: see
                            // `preserveSort`.
                            if (col.source === "gridOptionsChanged") {
                                applyColumnState(selectedView || savedViews[0], { preserveSort: true });
                            }
                            setIsViewModified(true);
                        }}
                        onColumnPinned={() => setIsViewModified(true)}
                        onFilterChanged={() => setIsViewModified(true)}
                        onColumnValueChanged={columnVisibilityChanged}
                        enableCellTextSelection={true}
                        getRowId={props.getRowId}
                        getRowClass={props.getRowClass}
                    />
                </div>
            </div>
            <SaveViewDialog
                show={saveViewDialogOpen}
                handleClose={() => setSaveViewDialogOpen(false)}
                handleSaveView={(viewName) => {
                    saveView(viewName);
                    setSaveViewDialogOpen(false);
                }}
                viewName={selectedView?.viewName || ""}
            />
        </Card>
    );
};
export default Grid;
